import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, ExternalLink } from 'lucide-react';
import api from '../api';
import ClientePicker from '../components/ClientePicker';
import { useClienteAttivo, ordinaClienti, clienteValido } from '../hooks/useClienteAttivo';
import { EmptyState, ListSkeleton } from '../components/ui';

// Stato campagna (da ads_stato, tollerante alle varianti di testo).
const STATO_CAMP = {
  attiva:      { bg:'var(--green-dim)', color:'var(--green)',      label:'Attiva' },
  pianificata: { bg:'var(--cyan-dim)',  color:'var(--cyan)',       label:'Pianificata' },
  pausa:       { bg:'var(--yellow-dim)',color:'var(--yellow)',     label:'In pausa' },
  conclusa:    { bg:'var(--bg3)',       color:'var(--text-muted)', label:'Conclusa' },
};
const statoKey = (s) => {
  const x = (s || '').toLowerCase();
  if (/attiv|in corso/.test(x)) return 'attiva';
  if (/pianific|program/.test(x)) return 'pianificata';
  if (/paus|stop|sospes/.test(x)) return 'pausa';
  return 'conclusa';
};
const fmtEuro = (n) => `€${(Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtData = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Senza data';

function BarraSpending({ speso, budget }) {
  const s = Number(speso) || 0, b = Number(budget) || 0;
  const pct = b > 0 ? Math.min(100, Math.round((s / b) * 100)) : (s > 0 ? 100 : 0);
  const over = b > 0 && s > b;
  const col = over ? 'var(--red)' : pct >= 80 ? 'var(--yellow)' : 'var(--cyan)';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:5, fontSize:12 }}>
        <span style={{ color:'var(--text-muted)' }}>Spending</span>
        <span style={{ fontVariantNumeric:'tabular-nums' }}>
          <b style={{ color: over ? 'var(--red)' : 'var(--text)' }}>{fmtEuro(s)}</b>
          <span style={{ color:'var(--text-muted)' }}> / {fmtEuro(b)}{b > 0 ? ` · ${pct}%` : ''}</span>
        </span>
      </div>
      <div style={{ height:6, borderRadius:4, background:'var(--bg3)', overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:col, borderRadius:4, transition:'width 0.2s' }} />
      </div>
    </div>
  );
}

function CampagnaCard({ c, onOpen }) {
  const s = STATO_CAMP[statoKey(c.ads_stato)];
  return (
    <div className="eden-card" style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:11 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <Megaphone size={16} color="var(--cyan)" style={{ flexShrink:0 }} />
        <span style={{ fontSize:14, fontWeight:700, color:'var(--text)', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {c.ads_campagna || 'Campagna'}
        </span>
        <span style={{ fontSize:11, fontWeight:600, padding:'2px 9px', borderRadius:7, background:s.bg, color:s.color, whiteSpace:'nowrap' }}>{s.label}</span>
        <span style={{ marginLeft:'auto', fontSize:12, color:'var(--text-muted)', fontVariantNumeric:'tabular-nums' }}>{fmtData(c.data_pubblicazione)}</span>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', fontSize:12, color:'var(--text-muted)' }}>
        {c.piattaforma && <span style={{ padding:'2px 8px', borderRadius:6, background:'var(--bg3)' }}>{c.piattaforma}</span>}
        {c.formato && <span style={{ padding:'2px 8px', borderRadius:6, background:'var(--purple-dim)', color:'var(--purple)' }}>{c.formato}</span>}
        <button onClick={() => onOpen(c)} title="Apri il contenuto collegato"
          style={{ marginLeft:'auto', display:'inline-flex', alignItems:'center', gap:5, background:'none', border:'none', cursor:'pointer', color:'var(--cyan)', fontSize:12, padding:0 }}>
          {c.titolo_post || 'Contenuto'} <ExternalLink size={13} />
        </button>
      </div>

      <BarraSpending speso={c.spending_speso} budget={c.spending_budget} />
    </div>
  );
}

export default function Campagne() {
  const navigate = useNavigate();
  const [clienti, setClienti] = useState([]);
  const [cliente, setCliente] = useClienteAttivo();
  const [camp, setCamp] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/clienti').then(r => {
      const lista = ordinaClienti(r.data || []);
      setClienti(lista);
      setCliente(clienteValido(lista, cliente));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cliente) { setCamp([]); return; }
    setLoading(true);
    api.get(`/ped?cliente_id=${cliente}&solo_campagne=1`)
      .then(r => setCamp(r.data || []))
      .catch(() => setCamp([]))
      .finally(() => setLoading(false));
  }, [cliente]);

  const pad2 = (n) => String(n).padStart(2, '0');
  const _now = new Date();
  const oggiStr = `${_now.getFullYear()}-${pad2(_now.getMonth() + 1)}-${pad2(_now.getDate())}`;
  const dataOf = (c) => (c.data_pubblicazione || '').slice(0, 10);
  const isConclusa = (c) => {
    const k = statoKey(c.ads_stato);
    if (!c.ads_stato) return dataOf(c) && dataOf(c) < oggiStr; // senza stato → per data
    return k === 'conclusa';
  };
  const attive = camp.filter(c => !isConclusa(c)).sort((a, b) => (dataOf(a) < dataOf(b) ? 1 : -1));
  const concluse = camp.filter(isConclusa).sort((a, b) => (dataOf(a) < dataOf(b) ? 1 : -1));

  const tot = (arr, k) => arr.reduce((a, c) => a + (Number(c[k]) || 0), 0);
  const totSpeso = tot(camp, 'spending_speso'), totBudget = tot(camp, 'spending_budget');

  const openCamp = (c) => navigate(`/ped/contenuto/${c.id}`);

  const Sezione = ({ titolo, arr }) => arr.length === 0 ? null : (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
        <span style={{ fontSize:12.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)' }}>{titolo}</span>
        <span style={{ fontSize:12, fontWeight:600, padding:'1px 8px', borderRadius:12, background:'var(--bg3)', color:'var(--text-muted)' }}>{arr.length}</span>
        <span style={{ fontSize:12, color:'var(--text-dim)', marginLeft:'auto', fontVariantNumeric:'tabular-nums' }}>
          {fmtEuro(tot(arr, 'spending_speso'))} / {fmtEuro(tot(arr, 'spending_budget'))}
        </span>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {arr.map(c => <CampagnaCard key={c.id} c={c} onOpen={openCamp} />)}
      </div>
    </div>
  );

  return (
    <div className="eden-page">
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
        <Megaphone size={26} color="var(--cyan)" />
        <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'var(--text)' }}>Campagne</h1>
      </div>
      <p style={{ margin:'0 0 20px 38px', color:'var(--text-muted)', fontSize:14 }}>Campagne ADS attive e concluse, con lo spending</p>

      <ClientePicker clienti={clienti} selected={cliente} onSelect={c => setCliente(c.id)} />

      {/* Riepilogo spending del cliente */}
      {camp.length > 0 && (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:22 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 18px' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:3 }}>Speso totale</div>
            <div style={{ fontSize:20, fontWeight:800, color:'var(--text)', fontVariantNumeric:'tabular-nums' }}>{fmtEuro(totSpeso)}</div>
          </div>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 18px' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:3 }}>Budget totale</div>
            <div style={{ fontSize:20, fontWeight:800, color:'var(--text)', fontVariantNumeric:'tabular-nums' }}>{fmtEuro(totBudget)}</div>
          </div>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 18px' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:3 }}>Campagne</div>
            <div style={{ fontSize:20, fontWeight:800, color:'var(--text)', fontVariantNumeric:'tabular-nums' }}>{attive.length} <span style={{ fontSize:13, fontWeight:500, color:'var(--text-muted)' }}>attive · {concluse.length} concluse</span></div>
          </div>
        </div>
      )}

      {loading ? (
        <ListSkeleton rows={4} />
      ) : camp.length === 0 ? (
        <EmptyState icon={Megaphone} title="Nessuna campagna"
          hint="Le righe del PED con una campagna ADS (con spending) compaiono qui. Aggiungi una campagna dal dettaglio contenuto." />
      ) : (
        <>
          <Sezione titolo="Attive e pianificate" arr={attive} />
          <Sezione titolo="Concluse" arr={concluse} />
        </>
      )}
    </div>
  );
}
